import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  Dialog as ConfirmDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography
} from "@mui/material";
import { useState } from "react";
import Iconify from "../../../minimals/components/iconify";
import { formatUTCDateWithLocalTime } from "../../../utils";
import { approveRemovalRequest, openProofDocument, reinstateRequest, rejectRemovalRequest } from "../shared";
import { RemovalRequest, RemovalRequestStatus } from "../types";

export type RemovalRequestMode = "review" | "completed";

type ReviewRemovalRequestModalProps = {
  open: boolean;
  handleClose: () => void;
  removalRequest: RemovalRequest;
  onActionComplete?: () => Promise<void>;
  BlacklistDetailsComponent?: React.ReactNode;
};

const ReviewRemovalRequestModal = ({
  open,
  handleClose,
  removalRequest,
  onActionComplete,
  BlacklistDetailsComponent
}: ReviewRemovalRequestModalProps) => {
  const mode = removalRequest.status === RemovalRequestStatus.Pending ? "review" : "completed";
  const {
    requestedByUser,
    requestedDate,
    reason,
    reviewedDate,
    entityId,
    status,
    reviewedByUser,
    proofUrl,
    entityType
  } = removalRequest;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await approveRemovalRequest(removalRequest);
      if (onActionComplete) await onActionComplete();
      handleClose();
    } catch (error) {
      console.error("Error approving removal request:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      await rejectRemovalRequest(removalRequest);
      if (onActionComplete) await onActionComplete();
      handleClose();
    } catch (error) {
      console.error("Error rejecting removal request:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReinstate = async () => {
    setIsProcessing(true);
    try {
      await reinstateRequest(removalRequest);
      if (onActionComplete) await onActionComplete();
      setConfirmOpen(false);
      handleClose();
    } catch (error) {
      console.error("Error reinstating record:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!proofUrl) return;
    openProofDocument(proofUrl, true);
  };

  const viewProofPdf = () => {
    if (!proofUrl) return;
    openProofDocument(proofUrl);
  };

  const getStatusColor = (status: RemovalRequestStatus) => {
    switch (status) {
      case RemovalRequestStatus.Pending:
        return "warning";
      case RemovalRequestStatus.Approved:
        return "success";
      case RemovalRequestStatus.Rejected:
        return "error";
      default:
        return "default";
    }
  };

  const renderDetails = () => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Removal Request Details
        </Typography>
        <Chip
          label={RemovalRequestStatus[removalRequest.status]}
          color={getStatusColor(removalRequest.status)}
          size="small"
        />
      </Box>

      <Grid container spacing={0.5}>
        <Grid item xs={6}>
          <Typography variant="body2">
            <strong>Requested By:</strong> {requestedByUser?.fullName || removalRequest.requestedBy}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2">
            <strong>Date:</strong> {formatUTCDateWithLocalTime(requestedDate)}
          </Typography>
        </Grid>

        {removalRequest.reviewedBy && (
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Reviewed By:</strong> {reviewedByUser?.fullName || removalRequest.reviewedBy}
            </Typography>
          </Grid>
        )}

        {reviewedDate && (
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Review Date:</strong> {formatUTCDateWithLocalTime(reviewedDate)}
            </Typography>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2" sx={{ mb: 0.25 }}>
          <strong>Reason:</strong>
        </Typography>
        <Typography variant="body2" sx={{ pl: 0.5 }}>
          {reason}
        </Typography>
      </Box>

      {proofUrl && (
        <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
            Proof:
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={viewProofPdf}
            sx={{ textTransform: "none", py: 0.25, px: 1, minWidth: "auto" }}
          >
            View
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: "none", ml: 1, py: 0.25, px: 1, minWidth: "auto" }}
          >
            <Iconify icon="eva:download-outline" width={16} />
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderActions = () => {
    if (mode === "review") {
      return (
        <>
          <Button onClick={handleReject} color="error" disabled={isProcessing} size="small">
            {isProcessing ? "Processing..." : "Reject"}
          </Button>
          <Button variant="contained" onClick={handleApprove} disabled={isProcessing} size="small">
            {isProcessing ? "Processing..." : "Approve"}
          </Button>
        </>
      );
    }

    if (mode === "completed") {
      return (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setConfirmOpen(true)}
          disabled={isProcessing}
          size="small"
        >
          {isProcessing ? "Processing..." : "Reinstate"}
        </Button>
      );
    }

    return null;
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1 }}>
          Removal Request
          <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 4 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          {BlacklistDetailsComponent}
          <Divider sx={{ my: 1 }} />
          {renderDetails()}
        </DialogContent>
        <DialogActions sx={{ py: 0.5 }}>{renderActions()}</DialogActions>
      </Dialog>

      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Reinstate</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you wish to reinstate this record to the blacklist?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isProcessing} size="small">
            No
          </Button>
          <Button variant="contained" onClick={handleReinstate} disabled={isProcessing} size="small">
            {isProcessing ? "Processing..." : "Yes"}
          </Button>
        </DialogActions>
      </ConfirmDialog>
    </>
  );
};

export default ReviewRemovalRequestModal;
